<script setup>
import { computed } from "vue";

const props = defineProps({
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  meta: {
    type: Object,
    default: () => ({ total: 0, per_page: 25, current_page: 1 }),
  },
});

const emit = defineEmits(["page-change"]);

const columns = [
  { sorter: false },
  {
    title: "Project ID",
    dataIndex: "project_code",
    key: "project_code",
    sorter: {
      compare: (a, b) =>
        (a.project_code || "").toLowerCase() >
        (b.project_code || "").toLowerCase()
          ? 1
          : -1,
    },
  },
  {
    title: "Project Name",
    dataIndex: "name",
    key: "name",
    sorter: {
      compare: (a, b) =>
        (a.name || "").toLowerCase() > (b.name || "").toLowerCase() ? 1 : -1,
    },
  },
  {
    title: "Leader",
    dataIndex: "leader_name",
    key: "leader_name",
    sorter: {
      compare: (a, b) =>
        (a.leader_name || "").toLowerCase() >
        (b.leader_name || "").toLowerCase()
          ? 1
          : -1,
    },
  },
  {
    title: "Team",
    dataIndex: "team",
    key: "team",
  },
  {
    title: "Deadline",
    dataIndex: "deadline",
    key: "deadline",
    sorter: {
      compare: (a, b) => ((a.deadline || "") > (b.deadline || "") ? 1 : -1),
    },
  },
  {
    title: "Priority",
    dataIndex: "priority",
    key: "priority",
    sorter: {
      compare: (a, b) =>
        (a.priority || "").toLowerCase() > (b.priority || "").toLowerCase()
          ? 1
          : -1,
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: {
      compare: (a, b) =>
        (a.status || "").toLowerCase() > (b.status || "").toLowerCase()
          ? 1
          : -1,
    },
  },
];

const dataSource = computed(() =>
  (props.items || []).map((p) => ({
    key: p.id,
    id: p.id,
    project_code: p.project_code,
    name: p.name,
    leader_name: p.leader_name,
    leader_avatar: p.leader_avatar || "user-32.jpg",
    team_avatars: p.team_avatars || [],
    extra_team_count: p.extra_team_count || 0,
    deadline: p.deadline,
    priority: p.priority,
    status: p.status,
  }))
);

const pagination = computed(() => ({
  current: props.meta.current_page || 1,
  pageSize: props.meta.per_page || 25,
  total: props.meta.total || dataSource.value.length,
  showSizeChanger: false,
  onChange: (page) => emit("page-change", page),
}));
</script>

<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :row-selection="{ onChange() {}, onSelect() {}, onSelectAll() {} }"
    :loading="loading"
    :pagination="pagination"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'name'">
        <div class="d-flex align-items-center file-name-icon">
          <h6 class="fw-medium mb-0">
            <a href="javascript:void(0);">{{ record.name }}</a>
          </h6>
        </div>
      </template>

      <template v-else-if="column.key === 'leader_name'">
        <div class="d-flex align-items-center file-name-icon">
          <a
            href="javascript:void(0);"
            class="avatar avatar-md border avatar-rounded"
          >
            <img :src="record.leader_avatar" class="img-fluid" alt="" />
          </a>
          <div class="ms-2">
            <h6 class="fw-normal fs-14 text-gray-5 mb-0">
              {{ record.leader_name || "-" }}
            </h6>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'team'">
        <div class="avatar-list-stacked avatar-group-sm">
           <span
            v-for="(av, i) in (record.team_avatars || []).slice(0, 3)"
            :key="i"
            class="avatar avatar-rounded"
          >
            <img
              class="border border-white"
              :src="av || require(`@/assets/img/profiles/avatar-02.jpg`)"
              alt="img"
            />
          </span>
          <span
            v-if="record.extra_team_count > 0"
            class="avatar avatar-rounded group-counts bg-primary rounded-circle border-0 fs-10"
          >
            +{{ record.extra_team_count }}
          </span>
        </div>
      </template>

      <template v-else-if="column.key === 'priority'">
        <span
          class="badge"
          :class="[
            record.priority === 'Low'
              ? 'badge-success-transparent'
              : record.priority === 'Medium'
              ? 'badge-warning-transparent'
              : 'badge-danger-transparent',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>{{ record.priority }}
        </span>
      </template>

      <template v-else-if="column.key === 'status'">
        <span
          class="d-inline-flex align-items-center badge-xs"
          :class="[
            'badge',
            record.status === 'Active' ? 'badge-success' : 'badge-danger',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>{{ record.status }}
        </span>
      </template>
    </template>
  </a-table>
</template>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 980px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
